import React, { FunctionComponent } from 'react';
import { MultiSelectOption } from '../../../../../global/multi-select-list/_multi-select-option';
import { IAttendeeInput, IFilteredAttendee, MeetingAttendeeIssue, MeetingAttendeeJoinMethod, MeetingAttendeeStatus } from '../../../../../../api/attendees.dao';
import VideoSVG from '../../../../../../images/svg/icons8-video-calls';
import AudioSVG from '../../../../../../images/svg/icons8-audio';
import PersonSVG from '../../../../../../images/svg/icons8-person';
import { IAttendeesListProps } from '../_attendees-list';
import Text from '../../../../../global/text/text';
import StatusButton from './_status-button/status-button';
import InfoButton from './_info-button/info-button';
import JoinedButton from './_joined-button/joined-button';
import './_attendee.scss';
import AttendeeNotes from './_attendee-notes/_attendee-notes';
import classnames from 'classnames';
import {
    RUN_MEETING_ATTENDEE_ISSUE_MAPPER,
    RUN_MEETING_ATTENDEE_JOIN_METHOD_MAPPER, RUN_MEETING_ATTENDEE_STATUS_MAPPER,
    RUN_MEETING_EVENTS
} from '../../../../../../tracking-service/tracking-values';

interface IProps extends IFilteredAttendee, IAttendeesListProps {
    organizationId: number;
    selectedAttendeeFilter?: boolean;
    organizationName?: string;
    markedPhone?: string | JSX.Element;
    handleTrackingEvent?: (event: RUN_MEETING_EVENTS, value?: string) => void;
}

interface IAttendeeAdditionalInfo {
    name: string;
    organizationName: string;
    title: string;
    email: string;
    tel: string;
}

interface IJoinMethodOption {
    label: string;
    id: string;
    value: MeetingAttendeeJoinMethod;
    icon: FunctionComponent;
}

const Attendee = (props: IProps) => {
    const {
        attendeeId,
        meetingAttendeeId,
        joinMethod,
        status = MeetingAttendeeStatus.CONFIRMED,
        phoneNumber,
        title,
        isLate,
        onChangeAttendee,
        meetingAttendeeNote,
        issues = [],
        withTableView,
        roleKey,
        organizationId,
        organizationName,
        removeWalkIn,
        attendeeIdToConnectionNameMap = {},
        onEditAttendee,
        selectedAttendeeFilter,
        markedPhone,
        isMeetingUnderway,
        handleTrackingEvent,
        ...infoButtonProps
    } = props;

    const isWalkIn = status === MeetingAttendeeStatus.WALK_IN;
    const isAttendeeJoined = status === MeetingAttendeeStatus.JOINED || isWalkIn;

    const handleToggleJoinStatus = () => {
        handleChangeStatus(isAttendeeJoined ? MeetingAttendeeStatus.CONFIRMED : MeetingAttendeeStatus.JOINED);
        if (handleTrackingEvent) {
            handleTrackingEvent(RUN_MEETING_EVENTS.ATTENDEE_JOIN_CHECK);
        }
    };

    const handleNoShowClick = (value: MeetingAttendeeStatus) => {
        if (status === value) {
            handleChangeStatus(MeetingAttendeeStatus.CONFIRMED);
        } else {
            handleChangeStatus(value);
        }
        if (handleTrackingEvent) {
            handleTrackingEvent(RUN_MEETING_EVENTS.REPORT_STATUS, RUN_MEETING_ATTENDEE_STATUS_MAPPER[value]);
        }
    };

    const attendeeInput: IAttendeeInput = {
        meetingAttendeeId: meetingAttendeeId,
        status: status,
        meetingAttendeeNote: meetingAttendeeNote,
        joinMethod: joinMethod,
        issues: issues,
        isLate: isLate
    };

    const handleChangeAttendee = (changedAttendeeInput: IAttendeeInput) => {
        onChangeAttendee(changedAttendeeInput, roleKey, organizationId, attendeeId);
    };

    const handleChangeStatus = newStatus => {
        handleChangeAttendee({
            ...attendeeInput,
            status: newStatus,
            joinMethod: null,
            isLate: false,
            issues: []
        });
    };

    const handleChangeJoinMethod = newJoinMethod => {
        const newStatus = !isAttendeeJoined ? MeetingAttendeeStatus.JOINED : status;
        handleChangeAttendee({
                ...attendeeInput,
                joinMethod: newJoinMethod,
                status: newStatus,
                issues: joinMethod === MeetingAttendeeJoinMethod.IN_PERSON
                    ? [] : attendeeInput.issues,
            });
        if (handleTrackingEvent) {
            handleTrackingEvent(RUN_MEETING_EVENTS.AV_SETUP, RUN_MEETING_ATTENDEE_JOIN_METHOD_MAPPER[newJoinMethod] || 'Unspecified');
        }
    };

    const handleChangeMeetingNote = (newNote: string) => {
        handleChangeAttendee({ ...attendeeInput, meetingAttendeeNote: newNote });
        if (handleTrackingEvent) {
            handleTrackingEvent(RUN_MEETING_EVENTS.ATTENDEE_NOTE);
        }
    };

    const handleChangeAttendeeIssue = (newIssues: MeetingAttendeeIssue[]) => {
        handleChangeAttendee({ ...attendeeInput, issues: newIssues });
        if (handleTrackingEvent) {
            handleTrackingEvent(
                RUN_MEETING_EVENTS.REPORT_ISSUE,
                newIssues.map(el => RUN_MEETING_ATTENDEE_ISSUE_MAPPER[el]).join(',')
            );
        }
    };

    const handleToggleLate = () => {
        handleChangeAttendee({ ...attendeeInput, isLate: !isLate });
        if (handleTrackingEvent) {
            handleTrackingEvent(RUN_MEETING_EVENTS.ATTENDEE_JOIN_LATE_CHECK);
        }
    };

    const handleAttendeeCopy = () => {
        if (handleTrackingEvent) {
            handleTrackingEvent(RUN_MEETING_EVENTS.COPY_ATTENDEE_NAME);
        }
    };

    const joinMethodOptions: IJoinMethodOption[] = [
        { label: 'Unspecified', id: 'oe.common.unspecified', value: null, icon: null},
        { label: 'Video', id: 'oe.common.video', value: MeetingAttendeeJoinMethod.VIDEO, icon: VideoSVG },
        { label: 'Audio', id: 'oe.common.audio', value: MeetingAttendeeJoinMethod.AUDIO, icon: AudioSVG },
        { label: 'In-Person', id: 'oe.common.inPerson', value: MeetingAttendeeJoinMethod.IN_PERSON, icon: PersonSVG }
    ];

    const openEditAttendeeSubWindow = () => {
        onEditAttendee(attendeeId);
    };

    return (
        <div className={classnames('attendees-list-row', {
            'attendees-list-row--expanded' : withTableView
        })}>
            <div className='attendees-list-item'>
                    <>
                        {!withTableView &&
                            <MultiSelectOption
                                isSelected={isAttendeeJoined}
                                isDisabled={isWalkIn}
                                onClick={handleToggleJoinStatus}
                            />
                        }
                        <div className='attendees-list-item__buttons'>
                            {withTableView &&
                            <MultiSelectOption
                                isSelected={isAttendeeJoined}
                                isDisabled={isWalkIn}
                                onClick={handleToggleJoinStatus}
                            />}
                            <InfoButton
                                handleToggleLate={handleToggleLate}
                                isLate={isLate}
                                isAttendeeJoined={isAttendeeJoined}
                                attendeeId={attendeeId}
                                openEditAttendeeSubWindow={openEditAttendeeSubWindow}
                                withTableView={withTableView}
                                onChangeJoinMethod={handleChangeJoinMethod}
                                status={status}
                                removeWalkIn={() => removeWalkIn(organizationId, attendeeId)}
                                selectedAttendeeFilter={selectedAttendeeFilter}
                                organizationName={organizationName}
                                handleAttendeeCopy={handleAttendeeCopy}
                                {...infoButtonProps}
                            />
                            {!withTableView &&
                                <JoinedButton
                                    joinMethodOptions={joinMethodOptions}
                                    onChangeJoinMethod={handleChangeJoinMethod}
                                    joinMethod={joinMethod}
                                    withTableView={withTableView}
                                    isMeetingUnderway={isMeetingUnderway}
                                    status={status}
                                />
                            }
                            {!withTableView && <StatusButton
                                joinMethod={joinMethod}
                                status={status}
                                isAttendeeJoined={isAttendeeJoined}
                                issues={issues}
                                onChangeAttendeeIssue={handleChangeAttendeeIssue}
                                meetingAttendeeNote={meetingAttendeeNote}
                                onChangeMeetingNote={handleChangeMeetingNote}
                                onNoShowClick={handleNoShowClick}
                                withTableView={withTableView}
                                isMeetingUnderway={isMeetingUnderway}
                            />}
                        </div>
                        {withTableView &&
                            <>
                                <div className='attendees-list-item__av'>
                                    <JoinedButton
                                        joinMethodOptions={joinMethodOptions}
                                        onChangeJoinMethod={handleChangeJoinMethod}
                                        joinMethod={joinMethod}
                                        withTableView={withTableView}
                                        isMeetingUnderway={isMeetingUnderway}
                                        status={status}
                                    />
                                </div>
                                <StatusButton
                                    joinMethod={joinMethod}
                                    status={status}
                                    isAttendeeJoined={isAttendeeJoined}
                                    issues={issues}
                                    onChangeAttendeeIssue={handleChangeAttendeeIssue}
                                    meetingAttendeeNote={meetingAttendeeNote}
                                    onChangeMeetingNote={handleChangeMeetingNote}
                                    onNoShowClick={handleNoShowClick}
                                    withTableView={withTableView}
                                    isMeetingUnderway={isMeetingUnderway}
                                />
                                <div className='attendees-list-item__connection'>
                                    <Text message={attendeeIdToConnectionNameMap[attendeeId]} withEllipsis />
                                </div>
                                <div className='attendees-list-item__phone'>
                                    {markedPhone ? <span title={phoneNumber}>{markedPhone}</span> : <Text message={phoneNumber} withEllipsis />}
                                </div>
                                <div className='attendees-list-item__title'>
                                    <Text message={title} withEllipsis />
                                </div>
                                <div className='attendees-list-item__notes'>
                                    <AttendeeNotes note={meetingAttendeeNote || ''} handleChangeMeetingNote={handleChangeMeetingNote}/>
                                </div>
                            </>
                        }
                    </>
            </div>
        </div>
    );
};

export { Attendee, IProps, IAttendeeAdditionalInfo, IJoinMethodOption };
